import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { fmtMoney, SHIPPING_TIERS, MAX_PILLS } from "@/lib/pharmacy";
import { Trash2, Minus, Plus } from "lucide-react";

export default function Cart() {
  const { items, removeItem, setQty, totals } = useCart();

  if (items.length === 0) {
    return (
      <div className="blz-container py-20 text-center">
        <h1 className="font-display text-3xl font-bold text-slate-900">Your cart is empty</h1>
        <p className="text-slate-500 mt-2">Browse our catalogue to find medications.</p>
        <Link to="/catalogue" className="btn-primary mt-6">Explore catalogue</Link>
      </div>
    );
  }
  const nextTier = SHIPPING_TIERS.find((t) => totals.totalPills <= t.max);

  return (
    <div className="blz-container py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Your cart</h1>
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 card-flat p-6">
          <ul className="divide-y divide-slate-100">
            {items.map((i) => (
              <li key={`${i.productId}-${i.packSize}`} className="py-4 flex gap-4" data-testid={`cart-row-${i.productId}`}>
                <img src={i.image} alt="" className="w-20 h-20 rounded-lg object-cover bg-slate-100"/>
                <div className="flex-1">
                  <Link to={`/product/${i.productId}`} className="font-semibold text-slate-900">{i.name}</Link>
                  <div className="text-xs text-slate-500">{i.generic} · {i.strength}</div>
                  <div className="mt-1 font-mono text-xs text-emerald-700">Pack of {i.packSize} pills</div>
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => setQty(i.productId, i.packSize, i.quantity - 1)} className="w-8 h-8 rounded-full border border-slate-200"><Minus className="h-3 w-3 mx-auto"/></button>
                    <span className="font-mono w-6 text-center">{i.quantity}</span>
                    <button onClick={() => setQty(i.productId, i.packSize, i.quantity + 1)} className="w-8 h-8 rounded-full border border-slate-200"><Plus className="h-3 w-3 mx-auto"/></button>
                    <button onClick={() => removeItem(i.productId, i.packSize)} className="ml-4 text-red-600 flex items-center gap-1 text-xs" data-testid={`cart-remove-${i.productId}`}><Trash2 className="h-3 w-3"/> Remove</button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-900 font-mono">{fmtMoney(i.unitPrice * i.quantity)}</div>
                  <div className="text-xs text-slate-500 font-mono">{fmtMoney(i.unitPrice)} each</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <aside className="card-flat p-6 h-fit sticky top-20">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Order summary</div>
          <div className="mb-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Pill count</span><span className="font-mono" data-testid="cart-total-pills">{totals.totalPills} / {MAX_PILLS}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${totals.error ? "bg-red-500" : "bg-emerald-600"}`} style={{ width: `${Math.min(100, (totals.totalPills / MAX_PILLS) * 100)}%` }}/>
            </div>
            {nextTier && !totals.error && (
              <div className="text-[11px] text-slate-500 mt-1 font-mono">Current tier: ${nextTier.cost} ({nextTier.label})</div>
            )}
          </div>
          {totals.error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3" data-testid="cart-page-error">{totals.error}</div>}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono">{fmtMoney(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span className="font-mono">{fmtMoney(totals.shipping)}</span></div>
            <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-slate-100"><span>Grand total</span><span className="font-mono" data-testid="cart-page-grand">{fmtMoney(totals.grand)}</span></div>
          </div>
          <Link to="/checkout" className={`btn-primary w-full mt-4 ${totals.error ? "pointer-events-none opacity-50" : ""}`} data-testid="cart-page-checkout">Proceed to checkout</Link>
        </aside>
      </div>
    </div>
  );
}
